import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getCurrentLanguage,
  today,
  validateDeliveryCode,
  validateOrderlineID,
  validateEmail,
  getDeliveryDeadline,
  formatDate,
  getAWSDate,
  yesterday,
  getCountryCode,
  getDateFormat,
  getCampaignStatusOptions,
} from "services/common";
import { Formik, Form as FormikForm } from "formik";
import * as yup from "yup";
import {
  Grid,
  Button,
  Icon,
  Input,
  Radio,
  Checkbox,
  Accordion,
  Menu,
  Popup,
  Select,
} from "semantic-ui-react";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { toast } from "components/Toast/Toast";
import {
  FieldRow,
  ErrorInfo,
  FieldInput,
  MultiSelectTagInputField,
  MultiSelectLanguageField,
} from "components/form";
import { getUnusedDeliveryCode } from "services/apiMiddleware";
import { getAgencys, getGroupedActiveNativeResolutions, getPrintSizes } from "store/selectors";
import LoadResolver from "components/LoadResolver";
import { CampaignAutomaticStatus, CampaignType, CampaignStatus } from "models";

const DeliveryCodeInput = ({ onChange, onError, isUpdatingCampaign, t, ...inputProps }) => {
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const generateDeliveryCode = useCallback(async () => {
    try {
      setIsGeneratingCode(true);
      const code = await getUnusedDeliveryCode();
      setIsGeneratingCode(false);
      onChange(code);
    } catch (error) {
      console.error(error);
      setIsGeneratingCode(false);
      onError(error.message);
    }
  }, [onChange, onError]);

  useEffect(() => {
    if (!isUpdatingCampaign) generateDeliveryCode();
  }, []);

  return isGeneratingCode ? ( // Show delivery code is being generated
    <Input
      className="delivery-code-input"
      disabled
      loading
      iconPosition="left"
      placeholder={t("campaign_form_generating_code")}
      {...inputProps}
    ></Input>
  ) : // Show generate-button when updating campaign and there is no delivery code
  isUpdatingCampaign && !inputProps.value ? (
    <>
      <Button type="button" onClick={() => generateDeliveryCode()}>
        {t("generate")}
      </Button>
      <br />
    </>
  ) : (
    // Show the delivery code when not loading
    <Input className="delivery-code-input" disabled {...inputProps} />
  );
};

const DigitalSizeAccordion = ({ value, onChange, groups }) => {
  const [activeIndex, setActiveIndex] = useState(-1);
  const orderedResolutions = (r1, r2) => {
    if (r1.type === r2.type) {
      return parseInt(r1.value.split("x")[0]) > parseInt(r2.value.split("x")[0]) ? 1 : -1;
    } else {
      return r1.type < r2.type ? 1 : -1;
    }
  };
  const orderedGroupsByResolution = useMemo(() => {
    return groups.sort(orderedResolutions);
  }, [groups]);
  const handleTitleClick = (e, titleProps) => {
    const { index } = titleProps;
    const newActiveIndex = activeIndex === index ? -1 : index;
    setActiveIndex(newActiveIndex);
  };

  const requiredResBoxIsIntermediate = (id) => {
    const nativeResNr = _.find(groups, ["id", id]).nativeResolutions.length;
    const digitalSize = _.find(value, ["requiredResolution", id]);
    return !digitalSize || _.includes([0, nativeResNr], digitalSize.nativeResolutions.length)
      ? false
      : true;
  };

  const toggleRequiredRes = (e, target) => {
    e.stopPropagation();
    const id = target.requiredresolution.id;
    const isChecked = target.checked;

    if (isChecked) {
      const nativeResolutions = _.map(target.requiredresolution.nativeResolutions, "id");
      const newValue = _.concat(value, { requiredResolution: id, nativeResolutions });
      onChange(newValue);
    } else {
      const newValue = _.filter(value, function (o) {
        return o.requiredResolution !== id;
      });
      onChange(newValue);
    }
  };

  const toggleNativeRes = (_e, target) => {
    const id = target.nativeresolution.id;
    const isChecked = target.checked;
    const requiredResID = target.nativeresolution.requiredResolutionID;
    const digitalSize = _.find(value, ["requiredResolution", requiredResID]);
    const digitalSizesWithoutRequiredRes = _.filter(value, function (o) {
      return o.requiredResolution !== requiredResID;
    });
    let newDigitalSizes;

    if (isChecked) {
      let newRequiredRes;
      // Add native resolution to required resolution
      if (digitalSize) {
        newRequiredRes = {
          ...digitalSize,
          nativeResolutions: _.concat(digitalSize.nativeResolutions, id),
        };
        // Required resolution doesn't exist -> create it with the native resolution
      } else {
        newRequiredRes = {
          requiredResolution: requiredResID,
          nativeResolutions: [id],
        };
      }
      newDigitalSizes = [...digitalSizesWithoutRequiredRes, newRequiredRes];
    } else {
      let newRequiredRes;
      const newNativeResolutions = _.without(digitalSize.nativeResolutions, id);
      // Remove native resolution from required resolution
      if (newNativeResolutions.length > 0) {
        newRequiredRes = {
          ...digitalSize,
          nativeResolutions: newNativeResolutions,
        };
        newDigitalSizes = [...digitalSizesWithoutRequiredRes, newRequiredRes];
        // Last native resolution was removed -> remove whole required resolution
      } else {
        newDigitalSizes = [...digitalSizesWithoutRequiredRes];
      }
    }
    onChange(newDigitalSizes);
  };

  const nativeResIsChecked = (nativeRes) => {
    const digitalSize = _.find(value, ["requiredResolution", nativeRes.requiredResolutionID]);
    return digitalSize ? _.includes(digitalSize.nativeResolutions, nativeRes.id) : false;
  };

  const NativeResolutionCheckboxes = ({ nativeResolutions }) => {
    return (
      <div>
        {nativeResolutions &&
          nativeResolutions.map((nativeResolution, i) => {
            return (
              <div key={i} className="native-resolution-row">
                <Checkbox
                  label={nativeResolution.value}
                  onChange={toggleNativeRes}
                  nativeresolution={nativeResolution}
                  checked={nativeResIsChecked(nativeResolution)}
                />
              </div>
            );
          })}
      </div>
    );
  };

  return (
    <div className="resolutions-selector">
      {groups &&
        orderedGroupsByResolution.map((group, i) => {
          return (
            <Accordion key={i} as={Menu} vertical exclusive={false}>
              <Menu.Item>
                <Accordion.Title active={activeIndex === i} index={i} onClick={handleTitleClick}>
                  <div className="required-resolution-row">
                    <ul>
                      <li>
                        {/* Required resolution checkbox */}
                        <Checkbox
                          label={`${group.value} (${
                            group.type === "IMAGE" ? "jpg/png" : "mp4/mov/jpg/png"
                          })`}
                          onChange={toggleRequiredRes}
                          requiredresolution={group}
                          checked={!!_.find(value, ["requiredResolution", group.id])}
                          indeterminate={requiredResBoxIsIntermediate(group.id)}
                        />
                      </li>
                    </ul>
                    <Icon className="caret right" />
                  </div>
                </Accordion.Title>
                <Accordion.Content active={activeIndex === i}>
                  <NativeResolutionCheckboxes nativeResolutions={group.nativeResolutions} />
                </Accordion.Content>
              </Menu.Item>
            </Accordion>
          );
        })}
    </div>
  );
};

const PrintSizesInput = ({ value, onChange, printsizes }) => {
  const toggle = (_e, target) => {
    const id = target.name;

    if (_.includes(value, id)) onChange(_.without(value, id));
    else onChange([...value, id]);
  };

  return (
    <div className="print-size-checkboxes">
      {_.map(printsizes, ({ id, name }) => (
        <Checkbox
          key={id}
          name={id}
          label={name}
          onChange={toggle}
          checked={!!_.includes(value, id)}
        />
      ))}
    </div>
  );
};

// Makes a list of a required resolution ids and its native resolution ids for the DigitalSizeAccordion
export const makeDigitalSizesList = (requiredResolutions, nativeResolutions) => {
  const digitalSizesList = _.map(requiredResolutions, function (requiredRes) {
    const requiredResolutionID = requiredRes.requiredResolutionID;
    const nativeResolutionsForRequiredRes = _.filter(nativeResolutions, function (nativeRes) {
      return nativeRes.nativeResolution.requiredResolutionID === requiredResolutionID;
    });
    const nativeResolutionIDs = _.map(nativeResolutionsForRequiredRes, "nativeResolutionID");
    return {
      requiredResolution: requiredRes.requiredResolutionID,
      nativeResolutions: nativeResolutionIDs,
    };
  });
  return digitalSizesList;
};

// Gets a list of CampaignRequiredResolution and CampaignNativeResolution ids to delete, and Required and NativeResolution ids to add
export const getCampaignUpdateResolutionLists = (
  originalRRs,
  originalNRs,
  selectedRRids,
  selectedNRids
) => {
  const campaignRRidsToDelete = _.reduce(
    originalRRs,
    function (result, { requiredResolutionID, id }) {
      return _.includes(selectedRRids, requiredResolutionID) ? result : [...result, id];
    },
    []
  );

  const originalRRids = _.map(originalRRs, "requiredResolutionID");
  const requiredResolutionsToAdd = _.filter(selectedRRids, function (id) {
    return !_.includes(originalRRids, id);
  });

  const campaignNRidsToDelete = _.reduce(
    originalNRs,
    function (result, { nativeResolutionID, id }) {
      return _.includes(selectedNRids, nativeResolutionID) ? result : [...result, id];
    },
    []
  );

  const originalNRids = _.map(originalNRs, "nativeResolutionID");
  const nativeResolutionsToAdd = _.filter(selectedNRids, function (id) {
    return !_.includes(originalNRids, id);
  });

  return {
    campaignRRidsToDelete,
    campaignNRidsToDelete,
    requiredResolutionsToAdd,
    nativeResolutionsToAdd,
  };
};

// Gets CampaignPrintSize ids to delete and PrintSize ids to add in campaign update
export const getCampaignUpdatePrintSizeChanges = (originalPrintSizes, selectedPrintSizeIds) => {
  const campaignPrintSizeIdsToDelete = _.reduce(
    originalPrintSizes,
    (result, { id, printSizeID }) => {
      return _.includes(selectedPrintSizeIds, printSizeID) ? result : [...result, id];
    },
    []
  );

  const originalPrintSizeIds = _.map(originalPrintSizes, "printSizeID");
  const printSizeIdsToAdd = _.filter(
    selectedPrintSizeIds,
    (id) => !_.includes(originalPrintSizeIds, id)
  );

  return {
    campaignPrintSizeIdsToDelete,
    printSizeIdsToAdd,
  };
};

function CampaignForm(props) {
  const { campaign, assets, onSuccess, onSuccessRunQC, goPrev } = props;
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const activeUser = useSelector((state) => state.activeUser.item) || {};
  const isAdmin = activeUser.isAdmin || activeUser.isSuperAdmin || activeUser.isPowerAdmin;
  useEffect(() => {
    if (isAdmin) {
      dispatch.requiredResolutions.load();
      dispatch.nativeResolutions.load();
      dispatch.printSizes.load();
      dispatch.agencys.load();
    }
  }, []);
  const agencys = useSelector(getAgencys);

  const mediaAgencies = (agencys.items ?? []).filter((agency) => agency.type === "MEDIA_AGENCY");
  const getMediaAgencyOptions = () => {
    return mediaAgencies.map((agency) => ({
      key: agency.id,
      value: agency.id,
      text: agency.name,
    }));
  };

  const getAgencyContactsById = (agencys, agencyId) => {
    const agency = agencys?.items?.find((a) => a.id === agencyId);
    const contacts = agency?.contacts;
    return Array.isArray(contacts) ? contacts : null;
  };

  const { isLoading, error, groups: nrGroups } = useSelector(getGroupedActiveNativeResolutions);
  const printSizes = useSelector(getPrintSizes);

  const isFieldDisabledForContributor = useMemo(
    () => !isAdmin && campaign && campaign.contract_number,
    [isAdmin, campaign]
  );

  const formSchema = yup.object().shape({
    name: yup.string().required(t("required")),
    advertiser: yup.string().required(t("required")),
    notes: yup.string().nullable(),
    scheduling_notes: yup.string().nullable(),
    start_time: yup
      .date()
      .min(today.toISOString().slice(0, 10), t("campaign_form_start_time_past_error"))
      .nullable()
      .default(null),
    end_time: yup
      .date()
      .min(today.toISOString().slice(0, 10), t("campaign_form_end_time_past_error"))
      .nullable()
      .default(null),
  });

  const adminSchema = yup.object().shape({
    name: yup.string().required(t("required")),
    advertiser: yup.string().required(t("required")),
    notes: yup.string().nullable(),
    adminNotes: yup.string().nullable(),
    scheduling_notes: yup.string().nullable(),
    sales_segment: yup.string().nullable(),
    start_time: yup.date().required(t("required")),
    end_time: yup.date().required(t("required")),
    contract_number: yup
      .string()
      .required(t("required"))
      .test(
        "is-delivery-code",
        t("campaign_form_delivery_code_validation_error"),
        validateDeliveryCode
      ),
    orderlineID: yup
      .string()
      .nullable()
      .test(
        "is-valid-orderlineID",
        t("campaign_form_orderlineID_validation_error"),
        validateOrderlineID
      ),
    salesperson_contact: yup
      .string()
      .nullable()
      .test("is-email", t("invalid_email"), validateEmail),
    mediaDeliveryPersonContacts: yup
      .array()
      .of(
        yup.string().test("is-email", (o) => `${o.value} : ${t("invalid_email")}`, validateEmail)
      ),
    type: yup
      .string()
      .oneOf([CampaignType.DIGITAL, CampaignType.PRINT])
      .nullable()
      .required(t("required")),
  });

  const schema = useMemo(() => (!isAdmin ? formSchema : adminSchema), [
    isAdmin,
    formSchema,
    adminSchema,
  ]);

  const initialSchedulingNote =
    getCountryCode() === "fi" ? t("default_scheduling_note_finnish_country_code") : "";

  const initialValues = useMemo(() => {
    // Creating campaign
    if (!campaign) {
      return {
        digitalSizes: [],
        printSizes: [],
        lowResolution: false,
        contract_number: "",
        name: "",
        advertiser: "",
        orderlineID: "",
        salesperson: "",
        salesperson_contact: "",
        mediaAgencyID: "",
        mediaDeliveryPersonContacts: [],
        notes: "",
        adminNotes: "",
        scheduling_notes: initialSchedulingNote,
        sales_segment: "",
        statusGroup: "ACTIVE",
      };
      // Updating campaign
    } else {
      return {
        ...campaign,
        type: campaign.type || null,
        start_time: campaign.start_time ? new Date(campaign.start_time) : undefined,
        end_time: campaign.end_time ? new Date(campaign.end_time) : undefined,
        deliveryDeadline: campaign.deliveryDeadline
          ? new Date(campaign.deliveryDeadline)
          : undefined,
        digitalSizes:
          campaign.type === CampaignType.DIGITAL
            ? makeDigitalSizesList(
                campaign.requiredResolutions.items,
                campaign.nativeResolutions.items
              )
            : [],
        printSizes: campaign.printSizes ? _.map(campaign.printSizes.items, "printSizeID") : [],
        videoLengths: campaign.duration ? campaign.duration.map(Number) : [],
        languages: campaign.languages || [],
        mediaDeliveryPersonContacts: campaign.mediaDeliveryPersonContacts || [],
        mediaAgencyID: campaign.mediaAgencyID || null,
        sales_segment: campaign.sales_segment || null,
      };
    }
  }, [campaign]);

  const [campaignType, setCampaignType] = useState(campaign ? campaign.type : null);
  const [campaignStartDate, setCampaignStartDate] = useState(
    campaign ? new Date(campaign.start_time) : null
  );
  const [autoDeliveryDeadline, setAutoDeliveryDeadline] = useState(null);

  useEffect(() => {
    campaignStartDate &&
      setAutoDeliveryDeadline(getDeliveryDeadline(new Date(campaignStartDate), campaignType));
  }, [campaignStartDate, campaignType]);

  const submit = async (values, actions) => {
    let errors;
    if (values.start_time && values.end_time && values.start_time > values.end_time) {
      errors = { ...errors, end_time: t("campaign_form_date_error") };
    }
    let deliveryDeadline;
    // Admin only checks
    if (isAdmin) {
      deliveryDeadline = values.deliveryDeadline || autoDeliveryDeadline;

      if (getAWSDate(deliveryDeadline) > getAWSDate(values.end_time)) {
        errors = {
          ...errors,
          deliveryDeadline: t("campaign_form_delivery_deadline_end_time_error"),
        };
      }
      // Contributor only checks
    } else {
      // Campaign's start and/or end times not set and no explanation in notes-field
      if ((!values.start_time || !values.end_time) && !values.notes) {
        errors = {
          ...errors,
          notes: t("campaign_form_time_and_notes_not_set_error"),
        };
      }
    }

    let automaticStatus = campaign
      ? values.automaticStatus ??
        campaign.automaticStatus ??
        CampaignAutomaticStatus.AWAITING_DELIVERY
      : CampaignAutomaticStatus.AWAITING_DELIVERY;

    const userChangedStatus = campaign && values.status !== campaign.status;

    // Check if status is COPY_MISSING and delivery deadline is changed to future
    if (campaign && campaign.status === CampaignStatus.COPY_MISSING) {
      deliveryDeadline = values.deliveryDeadline || autoDeliveryDeadline;
      if (getAWSDate(deliveryDeadline) > getAWSDate(new Date())) {
        automaticStatus = CampaignAutomaticStatus.AWAITING_DELIVERY;
        if (!userChangedStatus) {
          values.status = CampaignStatus.AWAITING_DELIVERY;
        }
      }
    }

    if (errors) {
      actions.resetForm({ values, errors });
      return;
    }

    let statusToSend;
    if (!campaign) {
      statusToSend = CampaignAutomaticStatus.AWAITING_DELIVERY;
    } else if (!values.status || values.status === "NULL") {
      statusToSend = automaticStatus;
    } else {
      statusToSend = values.status;
    }

    try {
      let props = {
        name: values.name,
        advertiser: values.advertiser,
        notes: values.notes,
        // Use scheduling notes value only for digital campaigns to prevent initial value from being saved for print campaings.
        scheduling_notes: values.type === CampaignType.DIGITAL ? values.scheduling_notes : "",
        start_time: values.start_time ? getAWSDate(values.start_time) : null,
        end_time: values.end_time ? getAWSDate(values.end_time) : null,
        deliveryDeadline: deliveryDeadline ? getAWSDate(deliveryDeadline) : null,
        duration: values.videoLengths ? values.videoLengths.map(String) : null,
        languages: values.languages ? values.languages : [],
        contract_number: values.contract_number,
        status: statusToSend,
        // Set statusGroup to ACTIVE when creating new item.
        statusGroup: "ACTIVE",
        automaticStatus: automaticStatus,
        type: values.type,
      };
      // Add admin only fields to props
      if (isAdmin) {
        props = {
          ...props,
          orderlineID: values.orderlineID,
          salesperson: values.salesperson,
          salesperson_contact: values.salesperson_contact,
          sales_segment: values.sales_segment ? values.sales_segment : "",
          mediaAgencyID: values.mediaAgencyID,
          // Get contacts from the correct agency
          mediaCollaborators: getAgencyContactsById(agencys, values.mediaAgencyID),
          mediaDeliveryPersonContacts: _.filter(
            values.mediaDeliveryPersonContacts,
            (v) => v !== "" // Filter empty values
          ),
          adminNotes: values.adminNotes,
        };
      }

      let requiredResolutions = undefined;
      let nativeResolutions = undefined;
      // Flatten digital sizes to separate lists for required resolutions and native resolutions
      if (values.type === CampaignType.DIGITAL) {
        requiredResolutions = _.map(values.digitalSizes, "requiredResolution");
        nativeResolutions = _.flatten(_.map(values.digitalSizes, "nativeResolutions"));
      }
      if (!campaign) {
        const newCampaign = await dispatch.campaigns.create({
          createProps: props,
          requiredResolutions,
          nativeResolutions,
          printSizes: values.type === CampaignType.PRINT ? values.printSizes : undefined,
        });
        toast(undefined, t("campaign_form_campaign_creation_success"));
        onSuccess({
          campaign: {
            ...newCampaign,
            ...(values.lowResolution ? { lowResolution: true } : {}),
          },
        });
      } else {
        let updatedCampaign;
        let runQCForAssets = false;
        if (values.type === CampaignType.DIGITAL) {
          const campaignUpdateResolutionLists = getCampaignUpdateResolutionLists(
            campaign.requiredResolutions.items,
            campaign.nativeResolutions.items,
            requiredResolutions,
            nativeResolutions
          );
          const {
            campaignRRidsToDelete,
            campaignNRidsToDelete,
            requiredResolutionsToAdd,
            nativeResolutionsToAdd,
          } = campaignUpdateResolutionLists;

          const resolutionChangesExist =
            campaignRRidsToDelete.length > 0 ||
            campaignNRidsToDelete.length > 0 ||
            requiredResolutionsToAdd.length > 0 ||
            nativeResolutionsToAdd.length > 0;

          // Run QC for campaign's assets if there were any resolution changes or new durations
          runQCForAssets = resolutionChangesExist || hasDurationChanged(values);

          updatedCampaign = await dispatch.campaigns.update({
            updateProps: { id: campaign.id, ...props },
            ...campaignUpdateResolutionLists,
          });
        } else {
          const campaignUpdatePrintSizeChanges = getCampaignUpdatePrintSizeChanges(
            campaign.printSizes.items,
            values.printSizes
          );

          updatedCampaign = await dispatch.campaigns.update({
            updateProps: { id: campaign.id, ...props },
            ...campaignUpdatePrintSizeChanges,
          });
        }
        runQCForAssets = runQCForAssets || hasLanguagesChanged(values);
        if (assets && assets.length > 0 && runQCForAssets) {
          const assetActions = _.map(assets, (asset) => ({ type: "runQC", props: { ...asset } }));
          toast(undefined, t("campaign_form_campaign_update_successful_with_qc"));
          onSuccessRunQC(assetActions);
        } else {
          toast(undefined, t("campaign_form_campaign_update_successful"));
          onSuccess({ campaign: updatedCampaign });
        }
      }
    } catch (error) {
      console.error(error);
      toast(
        "error",
        campaign
          ? t("campaign_form_campaign_update_failed")
          : t("campaign_form_campaign_creation_failed")
      );
    }
  };

  const hasDurationChanged = (values) => {
    const initial = campaign.duration ? campaign.duration.map(Number).sort() : [];
    const current = values.videoLengths ? values.videoLengths.map(Number).sort() : [];
    return initial.length !== current.length || initial.some((val, i) => val !== current[i]);
  };

  const hasLanguagesChanged = (values) => {
    const initial = campaign.languages ? [...campaign.languages].sort() : [];
    const current = values.languages ? [...values.languages].sort() : [];
    return initial.length !== current.length || initial.some((val, i) => val !== current[i]);
  };

  return (
    <LoadResolver isLoading={isLoading || printSizes.isLoading} error={error || printSizes.error}>
      <Formik validationSchema={schema} initialValues={initialValues} onSubmit={submit}>
        {({ submitCount, errors, isSubmitting, setFieldValue, setFieldError, values }) => (
          <FormikForm>
            <h2>
              {campaign ? (
                <span>
                  {t("campaign_form_edit_campaign")} <i>{campaign.name}</i>
                </span>
              ) : (
                t("campaign_form_new_campaign")
              )}
            </h2>
            <h3>{t("notice")}</h3>
            <Grid columns={2} verticalAlign="middle">
              <FieldRow
                label={t("campaign_name")}
                data-test="campaign_name-input"
                name="name"
                disabled={isFieldDisabledForContributor}
              />
              <FieldRow
                label={t("campaign_advertiser")}
                data-test="campaign_advertiser-input"
                name="advertiser"
                disabled={isFieldDisabledForContributor}
              />
              {isAdmin && (
                <Grid.Row className="form-field-row">
                  <Grid.Column width={3} textAlign="right">
                    {t("campaign_form_campaign_type")}
                  </Grid.Column>
                  <Grid.Column width={13}>
                    <div className="space-hori">
                      <Radio
                        type="radio"
                        name="type"
                        value={CampaignType.DIGITAL}
                        label="Digital"
                        data-test="digital_campaign_type_radio_button"
                        onChange={(e, { name, value, checked }) => {
                          if (checked) {
                            setFieldValue(name, value);
                            setFieldValue("printSizes", []);
                            setCampaignType(value);
                          }
                        }}
                        checked={values.type === CampaignType.DIGITAL}
                        disabled={!!(campaign && campaign.type)}
                      />
                      <Radio
                        type="radio"
                        name="type"
                        value={CampaignType.PRINT}
                        label="Print"
                        data-test="print_campaign_type_radio_button"
                        onChange={(e, { name, value, checked }) => {
                          if (checked) {
                            setFieldValue(name, value);
                            setFieldValue("digitalSizes", []);
                            setCampaignType(value);
                          }
                        }}
                        checked={values.type === CampaignType.PRINT}
                        disabled={!!(campaign && campaign.type)}
                      />
                    </div>
                  </Grid.Column>
                </Grid.Row>
              )}
              <Grid.Row className="form-field-row">
                <Grid.Column width={3} textAlign="right">
                  {t("campaign_form_campaign_time")}
                </Grid.Column>
                <Grid.Column width={13}>
                  <div className="space-hori">
                    <FieldInput
                      label={t("campaign_start_time")}
                      name="start_time"
                      type="date"
                      locale={getCurrentLanguage()}
                      format={getDateFormat()}
                      firstDayOfWeek={1}
                      autoComplete="off"
                      // Weird bug with react-semantic-ui-datepickers where minDate has to be yesterday for today to be selectable
                      minDate={!isAdmin && yesterday}
                      maxDate={values.end_time}
                      onChange={(_e, { name, value }) => {
                        setFieldValue(name, value);
                        setCampaignStartDate(value);
                      }}
                      disabled={isFieldDisabledForContributor}
                      clearable={!isFieldDisabledForContributor}
                    />
                    <FieldInput
                      label={t("campaign_end_time")}
                      name="end_time"
                      type="date"
                      locale={getCurrentLanguage()}
                      firstDayOfWeek={1}
                      format={getDateFormat()}
                      autoComplete="off"
                      minDate={values.start_time}
                      onChange={(_e, { name, value }) => setFieldValue(name, value)}
                      disabled={isFieldDisabledForContributor}
                      clearable={!isFieldDisabledForContributor}
                    />
                  </div>
                </Grid.Column>
              </Grid.Row>
              {isAdmin && (
                <>
                  <Grid.Row className="form-field-row">
                    <Grid.Column width={3} textAlign="right">
                      <label htmlFor="deliveryDeadline">{t("campaign_deliveryDeadline")}</label>
                    </Grid.Column>
                    <Grid.Column width={13}>
                      <FieldInput
                        name="deliveryDeadline"
                        type="date"
                        locale={getCurrentLanguage()}
                        format={getDateFormat()}
                        firstDayOfWeek={1}
                        autoComplete="off"
                        maxDate={values.end_time}
                        placeholder={
                          autoDeliveryDeadline ? formatDate(autoDeliveryDeadline) : getDateFormat()
                        }
                        onChange={(_e, { name, value }) => setFieldValue(name, value)}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row className="form-field-row">
                    <Grid.Column width={3} textAlign="right">
                      <label htmlFor="contract_number">{t("campaign_contract_number")}</label>
                    </Grid.Column>
                    <Grid.Column width={13}>
                      <DeliveryCodeInput
                        name="contract_number"
                        value={values.contract_number}
                        error={!!errors.contract_number}
                        fluid
                        onChange={(value) => {
                          setFieldValue("contract_number", value);
                        }}
                        onError={(error) => {
                          setFieldError("contract_number", error);
                        }}
                        isUpdatingCampaign={!!campaign}
                        t={t}
                      />
                      <span className="help-text">
                        {t("campaign_form_delivery_code_generation")}
                      </span>
                    </Grid.Column>
                  </Grid.Row>
                  {campaign && (
                    <Grid.Row className="form-field-row">
                      <Grid.Column width={3} textAlign="right">
                        <label htmlFor="campaign_status">{t("campaign_status")}</label>
                      </Grid.Column>
                      <Grid.Column width={13}>
                        <Select
                          options={getCampaignStatusOptions(values.type, values.status)}
                          value={values.status}
                          onChange={(_, data) => setFieldValue("status", data.value)}
                        />
                      </Grid.Column>
                    </Grid.Row>
                  )}
                  <Grid.Row className="form-field-row">
                    <Grid.Column width={3} textAlign="right">
                      {t(
                        values.type === CampaignType.PRINT
                          ? "campaign_form_campaign_print_asset_sizes"
                          : "campaign_form_campaign_asset_sizes"
                      )}
                    </Grid.Column>
                    <Grid.Column width={13}>
                      {values.type === "DIGITAL" ? (
                        nrGroups && nrGroups.length > 0 ? (
                          <DigitalSizeAccordion
                            value={values.digitalSizes}
                            onChange={(val) => {
                              setFieldValue("digitalSizes", val);
                            }}
                            groups={nrGroups}
                          />
                        ) : (
                          t("campaign_form_campaign_no_digital_sizes_defined")
                        )
                      ) : values.type === "PRINT" ? (
                        printSizes && printSizes.items.length > 0 ? (
                          <div className="space-hori">
                            <PrintSizesInput
                              value={values.printSizes}
                              onChange={(val) => {
                                setFieldValue("printSizes", val);
                              }}
                              printsizes={_.sortBy(printSizes.items, "name")}
                            />
                          </div>
                        ) : (
                          t("campaign_form_campaign_no_print_sizes_defined")
                        )
                      ) : (
                        t("campaign_form_campaign_select_campaign")
                      )}
                    </Grid.Column>
                  </Grid.Row>

                  {values.type === "DIGITAL" && (
                    <MultiSelectTagInputField
                      tags={[5, 6, 10]}
                      name="videoLengths"
                      label={t("campaign_video_durations")}
                      onChange={(updatedValues) => setFieldValue("videoLengths", updatedValues)}
                      value={values.videoLengths}
                    />
                  )}

                  {["be", "dev"].includes(getCountryCode()) && (
                    <MultiSelectLanguageField
                      name="languages"
                      label={t("campaign_languages")}
                      value={values.languages}
                      onChange={(updatedValues) => setFieldValue("languages", updatedValues)}
                    />
                  )}

                  <FieldRow
                    label={t("campaign_orderlineID")}
                    name="orderlineID"
                    value={values.orderlineID}
                    error={!!errors.orderlineID}
                    fluid
                    onChange={(e) => {
                      setFieldValue("orderlineID", e.target.value);
                    }}
                    onError={(error) => {
                      setFieldError("orderlineID", error);
                    }}
                  />
                  <FieldRow label={t("campaign_salesperson")} name="salesperson" />
                  <FieldRow label={t("campaign_salesperson_contact")} name="salesperson_contact" />
                  <FieldRow label={t("campaign_sales_segment")} name="sales_segment" />
                  <Grid.Row className="form-field-row">
                    <Grid.Column width={3} textAlign="right">
                      <label>{t("campaign_media_agency")}</label>
                    </Grid.Column>
                    <Grid.Column width={13}>
                      <Select
                        options={getMediaAgencyOptions()}
                        clearable
                        placeholder={t("campaign_select_media_agency")}
                        value={values.mediaAgencyID}
                        onChange={(_, data) => {
                          setFieldValue("mediaAgencyID", data.value || null);
                        }}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <FieldRow
                    label={t("campaign_mediaDeliveryPersonContacts")}
                    name="mediaDeliveryPersonContacts"
                    type="area"
                    placeholder={t("campaign_form_media_delivery_person_contact_help_text")}
                    valueAccessor={(val) => _.join(val, ",")}
                    onChange={(_e, { value, name }) =>
                      setFieldValue(
                        name,
                        _.map(_.split(value, ","), _.trim) // Split string into array of emails and trim whitespace
                      )
                    }
                    style={{ marginBottom: "-5px" }}
                  >
                    <span className="help-text">
                      {t("campaign_form_media_delivery_person_contact_help_text")}
                    </span>
                  </FieldRow>
                  <FieldRow label={t("campaign_adminNotes")} name="adminNotes" type="area" />
                </>
              )}
              {values.type === "DIGITAL" && (
                <FieldRow
                  label={t("campaign_scheduling_notes")}
                  name="scheduling_notes"
                  type="area"
                />
              )}
              {(campaign || !isAdmin) && (
                <FieldRow
                  label={t("campaign_notes")}
                  name="notes"
                  type="area"
                  data-test="campaign_notes-input"
                />
              )}
              {!isAdmin && !campaign && values.type === CampaignType.PRINT && (
                <Grid.Row className="form-field-row">
                  <Grid.Column width={3} textAlign="right" />
                  <Grid.Column width={13}>
                    <Checkbox
                      id="campaign_low_resolution"
                      label="Low Resolution"
                      checked={Boolean(values.lowResolution)}
                      onChange={(_e, { checked }) =>
                        setFieldValue("lowResolution", Boolean(checked))
                      }
                    />
                    <Popup
                      content={t("add_print_asset_lowres_hover_text")}
                      trigger={
                        <Icon
                          name="info circle"
                          style={{
                            marginLeft: "0.5rem",
                            cursor: "pointer",
                          }}
                        />
                      }
                      position="top center"
                    />
                  </Grid.Column>
                </Grid.Row>
              )}
            </Grid>
            <ErrorInfo errors={errors} submitCount={submitCount} t={t} tPrefix={"campaign_"} />
            <div className="form-footer">
              {goPrev && (
                <Button type="button" primary onClick={goPrev}>
                  {t("campaign_form_back_button")}
                </Button>
              )}
              <Button
                primary
                type="submit"
                loading={isSubmitting}
                data-test="campaign_form_next_button"
              >
                {t("campaign_form_next_button")}
              </Button>
            </div>
          </FormikForm>
        )}
      </Formik>
    </LoadResolver>
  );
}

export default CampaignForm;
